// components/Churches/CoverImage.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineClose, AiFillEdit } from "react-icons/ai";
import GalleryPickerField from "../../components/ImageGallery/GalleryPickerField";
import { AddChurchHooks } from "../../screens/Churches/hooks/Churchhooks";
import {
  saveChurchCoverImageRequest,
  deleteChurchCoverImageRequest,
} from "../../store/ChurchCoverImage/churchCoverImageActions";
import { fetchChurchesRequest } from "../../store/Churches/actions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import ConfirmToast from "../../components/universal/ConfirmToast";
const CoverImage = ({ data, church, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [activeTab, setActiveTab] = useState("desktop");
  const [objectFit, setObjectFit] = useState({
    desktop: "contain",
    tablet: "contain",
    mobile: "contain",
  });
  const [isDefault, setIsDefault] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTab, setPreviewTab] = useState("desktop"); // Separate tab for preview modal
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const selectedImageRef = useRef(null);
  const imgRef = useRef(null);

  // 🔹 Redux store: all churches
  const churches = useSelector((state) => state.churches.churches);
  const [refreshFlag, setRefreshFlag] = useState(false);

  // ✅ Re-fetch churches on load and whenever refreshFlag changes
  useEffect(() => {
    dispatch(fetchChurchesRequest({}, () => {}));
  }, [dispatch, refreshFlag]);

  // ✅ Extract current church using ID from prop
  const currentChurch = useMemo(() => {
    const ChurchObjId =church?._id
    if (!ChurchObjId || !Array.isArray(churches)) return null;
    return churches.find((c) => c._id === ChurchObjId) || church;
  }, [churches, church]);

  // ✅ Initialize AddChurchHooks
  const { tumbNailUrlsInputs, setTumbNailUrlsInputs, setimageSet } =
    AddChurchHooks(setLoading, data);

  // ✅ Format gallery data dynamically from Redux store - FIXED
  const formattedGallery = useMemo(() => {
    const covers = currentChurch?.churchcoverimages || [];

    return covers.map((cover) => {
      // Check if gallery exists and has the image
      const gallery = cover.gallery || {};


      return {
        _id: cover.coverimageObjId || cover._id,
        name: gallery?.imageName || "Cover Image",
        // Main image URLs from gallery
        web: {
          url: gallery?.imageUrl || "",
          title: gallery?.imageName || "Cover Image"
        },
        // Check for device-specific URLs
        tablet: {
          url: gallery?.imageUrlTablet || gallery?.tabImageUrl || gallery?.imageUrl || "",
          title: gallery?.imageName || "Cover Image"
        },
        mobile: {
          url: gallery?.imageUrlMobile || gallery?.mobileImageUrl || gallery?.imageUrl || "",
          title: gallery?.imageName || "Cover Image"
        },
        // Settings for each device view
        mobilesetting: cover.mobilesetting || "contain",
        tabletsetting: cover.tabletsetting || "contain",
        dashboardsetting: cover.dashboardsetting || "contain",
        default: cover.default === "true",
        // Keep the raw gallery object for reference
        raw: gallery,
        // For edit functionality
        galleryObjId: gallery?._id || "",
        coverSettings: {
          mobilesetting: cover.mobilesetting,
          tabletsetting: cover.tabletsetting,
          dashboardsetting: cover.dashboardsetting,
          default: cover.default
        }
      };
    });
  }, [currentChurch]);


  // ✅ When image selected from gallery picker
  const handleGallerySelect = (imgEvent) => {
    if (!imgEvent?.target?.value) {
      toast.warn("No valid image selected!");
      return;
    }
    const img = imgEvent.target.value;
    const webUrl = img.web?.url || img.tablet?.url || img.mobile?.url;

    setTumbNailUrlsInputs({
      webUploadUrl: webUrl,
      tabletUploadUrl: img.tablet?.url || webUrl,
      mobileUploadUrl: img.mobile?.url || webUrl,
    });

    setimageSet(img);
    selectedImageRef.current = { target: { value: img } };
  };

  // ✅ Save cover image
  const handleAutoSave = (selectedImage) => {
        const churchObjId = church?._id || data?._id ;

    const coverImagePayload = {
      _id: churchObjId,
      galleryObjId: selectedImage?.[0]?.id || selectedImage.raw?._id,
      mobilesetting: selectedImage?.mobilesetting || "contain",
      tabletsetting: selectedImage?.tabletsetting || "contain",
      dashboardsetting: selectedImage?.dashboardsetting || "contain",
      default: selectedImage?.default ? "true" : "false",
    };


    setSaving(true);
    dispatch(
      saveChurchCoverImageRequest({
        apiPayloadRequest: coverImagePayload,
        callback: (res) => {
          setSaving(false);
          if (res?.type === 1) {
            toast.success("Cover image saved successfully!");
            setRefreshFlag((prev) => !prev);
            setResetTrigger((prev) => prev + 1);
          } else {
            toast.error("Failed to save cover image: " + (res?.error || "Unknown error"));
          }
        },
      })
    );
  };

  // ✅ Delete cover image
  const handleDeleteCoverImage = (img) => {
    const churchObjId = church?._id || data?._id;

    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmToast
          title="Delete Cover Image"
          message="Are you sure you want to delete this cover image? This action cannot be undone."
          onClose={onClose}
          onSuccess={() => {
            // ✅ CLOSE DIALOG FIRST
            onClose();

            // ✅ THEN CALL API
            dispatch(
              deleteChurchCoverImageRequest({
                apiPayloadRequest: {
                  _id: churchObjId,
                  coverimageObjId: img._id,
                },
                callback: (res) => {
                  if (res?.type === 1) {
                    toast.success("Cover image deleted!");
                    setRefreshFlag((prev) => !prev);
                  } else {
                    toast.error(
                      "Failed to delete: " + (res?.error || "Unknown error")
                    );
                  }
                },
              })
            );
          }}
        />
      ),
    });
  };

  // ✅ Handle image preview click
  const handleImagePreview = (img) => {
    setSelectedImage(img);
    setPreviewTab("desktop"); // Default to desktop tab
    setShowPreviewModal(true);
  };

  // ✅ Handle edit click
  const handleEdit = (img) => {
        // Open the preview modal with edit mode
    setPreviewImage(img);
    setActiveTab("desktop");
    setObjectFit({
      desktop: img.dashboardsetting || "contain",
      tablet: img.tabletsetting || "contain",
      mobile: img.mobilesetting || "contain",
    });
    setIsDefault(img.default);
  };

  // ✅ Close preview modal
  const closePreviewModal = () => {
    setPreviewImage(null);
    setActiveTab("desktop");
    setObjectFit({ desktop: "contain", tablet: "contain", mobile: "contain" });
    setIsDefault(false);
  };

  // ✅ Get image URL based on current preview tab
  const getPreviewImageUrl = () => {
    if (!selectedImage) return "";

    switch(previewTab) {
      case "desktop":
        return selectedImage.web?.url || "";
      case "tablet":
        return selectedImage.tablet?.url || selectedImage.web?.url || "";
      case "mobile":
        return selectedImage.mobile?.url || selectedImage.web?.url || "";
      default:
        return selectedImage.web?.url || "";
    }
  };

  // ✅ Handle image load to get dimensions
  const handleImageLoad = (e) => {
    const img = e.target;
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
  };

  // ✅ Calculate modal width based on image dimensions
  const getModalWidth = () => {
    const maxWidth = window.innerWidth * 0.9; // 90% of viewport width
    const maxHeight = window.innerHeight * 0.8; // 80% of viewport height

    if (imageDimensions.width === 0 || imageDimensions.height === 0) {
      return "max-w-[90vw]";
    }

    // Calculate aspect ratio
    const aspectRatio = imageDimensions.width / imageDimensions.height;

    // Calculate width based on height constraint
    const widthFromHeight = maxHeight * aspectRatio;

    // Use the smaller of the two constraints
    const finalWidth = Math.min(maxWidth, widthFromHeight);

    return `max-w-[${Math.min(finalWidth, 1200)}px]`;
  };

  const renderPreview = (aspectClass, tab) => {
    const tabKey = tab === "desktop" ? "web" : tab;
    if (!previewImage?.[tabKey]?.url) return null;
    return (
      <div className={`${aspectClass} w-full max-w-[600px] border border-gray-700 rounded-md overflow-hidden`}>
        <img
          src={previewImage[tabKey].url}
          alt={previewImage.name}
          className={`w-full h-full object-${objectFit[tab] || "contain"}`}
        />
      </div>
    );
  };

  return (
    <>
    {
      church ?
    <div className="px-6">
      {/* ✅ Gallery Picker */}
      <GalleryPickerField
        label="Cover Image"
        name="galleryImage"
        church={church}
        handleChange={handleGallerySelect}
        autoSave={true}
        onAutoSave={handleAutoSave}
        resetTrigger={resetTrigger}
      />

      {/* ✅ Gallery Grid - Display like Image Gallery */}
      <div className="p-6 max-h-[70vh] overflow-y-auto custom-scroll">
        {formattedGallery.length > 0 ? (
          <div className="flex flex-wrap gap-8 mt-6 justify-start">
            {formattedGallery.map((img, index) => (
              <div key={img._id || index} className="relative group">
                {/* MAIN IMAGE */}
                <img
                  src={img.web?.url || "/placeholder.jpg"}
                  alt="Cover"
                  className="w-[180px] h-[120px] object-cover rounded-lg border border-gray-700 shadow-md cursor-pointer transition-transform duration-300 hover:scale-[1.03]"
                  onClick={() => handleImagePreview(img)}
                />

                {/* ====== RESPONSIVE VERSION MINI PREVIEWS ====== */}
                <div className="absolute -top-8 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Tablet preview circle */}
                  {img.tablet?.url && (
                    <img
                      src={img.tablet.url}
                      title="Tablet Image"
                      className="w-6 h-6 rounded-full border border-white shadow object-cover cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(img);
                        setPreviewTab("tablet");
                        setShowPreviewModal(true);
                      }}
                    />
                  )}
                </div>

                {/* Mobile preview circle */}
                <div className="absolute top-2 -right-8 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {img.mobile?.url && (
                    <img
                      src={img.mobile.url}
                      title="Mobile Image"
                      className="w-6 h-6 rounded-full border border-white shadow object-cover cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(img);
                        setPreviewTab("mobile");
                        setShowPreviewModal(true);
                      }}
                    />
                  )}
                </div>
                {/* ====== END MINI PREVIEWS ====== */}

                {/* Title */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-[10px] px-2 py-0.5 rounded-md text-white truncate max-w-[160px]">
                  {img.name}
                </div>

                {/* Edit button */}
                <div
                  className="absolute bottom-2 left-2 bg-black/50 p-1 rounded-full cursor-pointer hover:bg-black/70 transition"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(img);
                  }}
                >
                  <AiFillEdit className="text-white w-4 h-4" />
                </div>

                {/* Delete button */}
                <div
                  className="absolute top-2 left-2 bg-black/50 p-1 rounded-full cursor-pointer hover:bg-black/70 transition"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCoverImage(img);
                  }}
                >
                  <AiOutlineClose className="text-white w-4 h-4" />
                </div>

                {/* Default badge */}
                {img.default && (
                  <div className="absolute top-2 right-2 bg-yellowish text-black text-[8px] px-1.5 py-0.5 rounded-full font-bold">
                    Default
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-greyish text-center py-10">No cover images found.</p>
        )}
      </div>

      {/* ✅ Preview Modal with Tabs (Single Close Icon) */}
      {selectedImage && showPreviewModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] px-4">
          <div className={`bg-gray-900 p-6 rounded-lg w-auto ${getModalWidth()} max-h-[90vh] overflow-auto relative`}>
            {/* Single Close Button at top-right */}
            <button
              className="absolute top-4 right-4 bg-black/60 p-2 rounded-full hover:bg-black transition z-50"
              onClick={() => {
                setSelectedImage(null);
                setShowPreviewModal(false);
              }}
            >
              <AiOutlineClose className="text-white w-5 h-5" />
            </button>

            {/* Image Name */}
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-white">{selectedImage.name}</h3>
              {selectedImage.default && (
                <span className="inline-block bg-yellowish text-black text-xs px-2 py-1 rounded-full font-bold ml-2">
                  Default Cover
                </span>
              )}
            </div>

            {/* Tabs for Desktop/Tablet/Mobile */}
            <div className="flex justify-center mb-6 gap-3">
              {["desktop", "tablet", "mobile"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setPreviewTab(tab)}
                  className={`px-4 py-2 rounded-md text-sm ${
                    previewTab === tab
                      ? "bg-yellowish text-black font-semibold"
                      : "bg-gray-700 text-white"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} View
                </button>
              ))}
            </div>

            {/* Image Preview - Auto-sizing */}
            <div className="flex justify-center mb-6">
              <div className="relative bg-black rounded-md overflow-hidden border border-gray-600">
                <img
                  ref={imgRef}
                  src={getPreviewImageUrl()}
                  alt={selectedImage.name}
                  className="max-w-full max-h-[65vh] object-contain"
                  onLoad={handleImageLoad}
                  style={{
                    width: "auto",
                    height: "auto",
                    maxWidth: "100%",
                    maxHeight: "65vh"
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Edit/Settings Modal (for changing object-fit and default settings) */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000]"
          onClick={(e) => e.target === e.currentTarget && closePreviewModal()}
        >
          <div className="bg-gray-900 p-6 rounded-lg w-auto max-w-[90vw] max-h-[90vh] overflow-auto relative">
            {/* Single Close Button */}
            <button
              className="absolute top-4 right-4 bg-black/60 p-2 rounded-full hover:bg-black transition z-50"
              onClick={closePreviewModal}
            >
              <AiOutlineClose className="text-white w-5 h-5" />
            </button>

            {/* Tabs */}
            <div className="flex justify-center mb-4 gap-3">
              {["desktop", "tablet", "mobile"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-md text-sm ${
                    activeTab === tab
                      ? "bg-yellowish text-black font-semibold"
                      : "bg-gray-700 text-white"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} View
                </button>
              ))}
            </div>

            {/* Object Fit Buttons */}
            <div className="flex justify-center mb-4 gap-2 flex-wrap">
              {["cover", "contain", "fill", "none", "scale-down"].map((fit) => (
                <button
                  key={fit}
                  onClick={() =>
                    setObjectFit((prev) => ({ ...prev, [activeTab]: fit }))
                  }
                  className={`px-3 py-1 text-xs rounded-md ${
                    objectFit[activeTab] === fit
                      ? "bg-yellowish text-black"
                      : "bg-gray-700 text-white"
                  }`}
                >
                  {fit}
                </button>
              ))}
            </div>

            {/* Preview - Auto-sizing */}
            <div className="relative flex justify-center mb-6">
              <div className="w-auto max-w-full">
                {activeTab === "desktop" && (
                  <div className="w-auto max-w-[800px] border border-gray-700 rounded-md overflow-hidden">
                    <img
                      src={previewImage?.web?.url || ""}
                      alt={previewImage?.name}
                      className="w-auto max-w-full h-auto max-h-[50vh] object-contain"
                    />
                  </div>
                )}
                {activeTab === "tablet" && (
                  <div className="w-auto max-w-[600px] border border-gray-700 rounded-md overflow-hidden">
                    <img
                      src={previewImage?.tablet?.url || previewImage?.web?.url || ""}
                      alt={previewImage?.name}
                      className="w-auto max-w-full h-auto max-h-[50vh] object-contain"
                    />
                  </div>
                )}
                {activeTab === "mobile" && (
                  <div className="w-auto max-w-[400px] border border-gray-700 rounded-md overflow-hidden">
                    <img
                      src={previewImage?.mobile?.url || previewImage?.web?.url || ""}
                      alt={previewImage?.name}
                      className="w-auto max-w-full h-auto max-h-[50vh] object-contain"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between px-6 py-3 bg-black/30 rounded-lg">
              <div className="flex items-center gap-2 text-white text-sm">
                <input
                  type="checkbox"
                  id="defaultCheckbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="w-4 h-4 cursor-pointer accent-yellowish"
                />
                <label htmlFor="defaultCheckbox" className="cursor-pointer select-none">
                  Set as Default
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={closePreviewModal}
                  className="bg-gray-700 text-white px-4 py-2 rounded-md font-medium hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!previewImage) return;

                    // Update the image settings
                    const churchObjId = church?._id || data?._id ;
                    const updatePayload = {
                      _id: churchObjId,
                      galleryObjId: previewImage.galleryObjId || previewImage.raw?._id,
                      coverimageObjId: previewImage._id,
                      mobilesetting: objectFit.mobile,
                      tabletsetting: objectFit.tablet,
                      dashboardsetting: objectFit.desktop,
                      default: isDefault ? "true" : "false",
                    };

                    // Dispatch update request
                    dispatch(
                      saveChurchCoverImageRequest({
                        apiPayloadRequest: updatePayload,
                        callback: (res) => {
                          if (res?.type === 1) {
                            toast.success("Cover image updated successfully!");
                            setRefreshFlag((prev) => !prev);
                            closePreviewModal();
                          } else {
                            toast.error("Failed to update cover image");
                          }
                        },
                      })
                    );
                  }}
                  className="bg-yellowish text-black px-5 py-2 rounded-md font-medium hover:bg-yellow-400 transition"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    :
    <div className="w-full h-full flex items-center justify-center text-gray-400 ">
     <p>Add Church/Ministry/Channel First</p>

    </div>
}
    </>
  );
};

export default CoverImage;